import {FC, useState} from 'react';

import {Box, Checkbox, Typography, TypographyProps, useMediaQuery} from '@mui/material';
import {styled, useTheme} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';

import BasketVoucherIcon from 'assets/icons/basketVoucherIcon';
import ChevronDown from 'assets/icons/chevronDown';
import ChevronRight from 'assets/icons/chevronRight';
import ThickChevronUp from 'assets/icons/thickChevronUp';
import VoucherDiscountIcon from 'assets/icons/voucherDiscountIcon';
import VoucherRemoveIcon from 'assets/icons/voucherRemoveIcon';
import {Button} from 'components/button/Button';
import {themeConfig} from 'config/theme';
import useAnalytics, {ANALYTICS, EVENT_SOURCE, SUGGESTEDLIST_CHANNELS} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import useGetAvailableVouchers from 'hooks/useGetAvailableVouchers';
import {useRemoveBasketVoucher} from 'hooks/useRemoveAppliedVoucher';
import {useSelectedItemsLocalStorage} from 'hooks/useSelectedItemsLocalStorage';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {formatPriceWithCurrency} from 'utils';
import {filter1PItems, filter3PItems} from 'utils/helper';
import {BasketMinimumOrderBanner} from 'views/basket-banners/components/BasketMinimumOrderBanner';
import {MinimumBasketAmountModal} from 'views/basket-modals/MinimumBasketAmountModal';
import RemoveVoucherModal from 'views/basket-modals/RemoveVoucherModal';

import {
  DeliveryFeesLabel,
  StyledBasketFooterWrapper,
  StyleFooterContentWrapper,
  VoucherDiscountContainer,
  VoucherDiscountText,
  VoucherLabel,
  VoucherSelectText,
  VoucherWrapper
} from './styles';
import {BasketDrawerFooterViewProps} from './types/basketDrawerFooter.type';
import {useAppMode} from 'hooks/useAppMode';
import {config} from 'config/env';

export const BasketTotalPriceV2 = styled(Typography)<TypographyProps>(({theme}) => ({
  fontWeight: theme.typography.h2.fontWeight,
  fontSize: theme.typography.h4.fontSize
}));

export const FooterView: FC<BasketDrawerFooterViewProps> = ({
  onCheckoutClick,
  actionButtonLabel,
  basketPrice,
  disableCheckoutBtn,
  loading,
  hasProductInBasket,
  deliveryFees,
  isLessThanMinSpend,
  selectedItems,
  onClickSelectAllItems,
  isSelectedAllBasketItems,
  setShowBasketSummaryModal,
  shouldShowMinBasketWarning,
  minimumBasket,
  showBasketSummaryModal,
  showDeliveryFee
}) => {
  const theme = useTheme();
  const {t} = useTranslation();
  const selectedItemsLocalStorage = useSelectedItemsLocalStorage();
  const basket = useAppSelector((state) => state?.basket?.basket);
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const [isMinimalBasketModalOpen, setIsMinimalBasketModalOpen] = useState<boolean>(false);
  const [voucherRemoved, setVoucherRemoved] = useState<boolean>(false);
  const [showVoucherRemovalModal, setShowVoucherRemovalModal] = useState<boolean>(false);
  const [removingVoucher, setRemovingVoucher] = useState<boolean>(false);
  const isReactNativeWebView = ReactNative.isReactNativeWebView();
  const analytics = useAnalytics();
  const {availableVouchers} = useGetAvailableVouchers();
  const allBasketItems = basket?.basketItems || [];
  const appliedVoucherPromotion = basket?.appliedVoucherPromotion;
  const {removeVoucher} = useRemoveBasketVoucher();
  const basketTotalPrice = basket?.total || 0;
  const {isExpress} = useAppMode();
  const notExpressOrEnabled = !isExpress || config.ENABLE_EXPRESS_OMNI_CHANNEL_VOUCHER;

  const isMobileScreen = isReactNativeWebView || isMobileView;

  const toggleMinimalBasketModal = () => {
    setIsMinimalBasketModalOpen((prev) => !prev);
  };

  const openBasketSummaryAnalytics = (eventName: string, payload: any) => {
    analytics.trackEvent(eventName, {
      storeId: basket?.storeCode || '',
      channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
      segment_source_name: isReactNativeWebView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
      basketSize: basket?.basketItems?.length || 0,
      onePCount: filter1PItems(basket?.basketItems || []).length || 0,
      threePCount: filter3PItems(basket?.basketItems || []).length || 0,
      ...payload
    });
  };

  const ChevronUpDownIcon = showBasketSummaryModal ? ChevronDown : ThickChevronUp;

  const discount = voucherRemoved
    ? 0
    : basket?.appliedVoucherPromotion?.reduce((total, voucher) => {
        return total + (voucher?.discount || 0);
      }, 0) || 0;

  const isOmniChannelEnabled = useAppSelector((state) => state?.basket?.omniChannelsVoucherRedemptionEnabled);
  const defaultMembershipCard = useAppSelector((state) => state?.app?.defaultMembershipCard);

  const handleRemoveVoucherClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowVoucherRemovalModal(true);
  };

  const handleConfirmRemoveVoucher = async () => {
    if (!appliedVoucherPromotion?.[0]?.voucherId) {
      return;
    }

    setRemovingVoucher(true);
    try {
      await removeVoucher(appliedVoucherPromotion[0].voucherId);
      setShowVoucherRemovalModal(false);
      setVoucherRemoved(true);
    } catch (error) {
      console.error('Failed to remove voucher:', error);
    } finally {
      setRemovingVoucher(false);
    }
  };

  const getPadding = () => {
    if (isMobileScreen) {
      return discount ? theme.spacing(0, 2, 0, 2) : theme.spacing(1.5, 2, 1.5, 2);
    }
    return discount ? theme.spacing(0, 4, 0, 4) : theme.spacing(2, 4, 2, 4);
  };

  return (
    <StyledBasketFooterWrapper>
      {hasProductInBasket && isOmniChannelEnabled && defaultMembershipCard?.cardNumber && notExpressOrEnabled && (
        <VoucherWrapper
          padding={getPadding()}
          onClick={() => {
            SendBrowserEvent.goToBasketVouchers(
              allBasketItems,
              appliedVoucherPromotion,
              availableVouchers,
              basketTotalPrice,
              selectedItemsLocalStorage.get()
            );
          }}
          sx={{cursor: isMobileScreen ? 'pointer' : 'default'}}
        >
          <Box display="flex" alignItems="center" gap={theme.spacing(1)}>
            <BasketVoucherIcon size={20} />
            <VoucherLabel variant="body2">{t('omniChannelVoucher.voucher')}</VoucherLabel>
          </Box>

          <Box display="flex" alignItems="center" gap={theme.spacing(0.5)}>
            {discount ? (
              <VoucherDiscountContainer>
                <VoucherDiscountIcon size={48} />
                <VoucherDiscountText>-{discount}฿</VoucherDiscountText>
              </VoucherDiscountContainer>
            ) : (
              <VoucherSelectText>
                {isReactNativeWebView ? t('omniChannelVoucher.select') : t('omniChannelVoucher.selectWeb')}
              </VoucherSelectText>
            )}
            {!!discount && !isMobileScreen && (
              <Box
                onClick={handleRemoveVoucherClick}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: theme.spacing(0.5),
                  '&:hover': {
                    opacity: 0.7
                  }
                }}
              >
                <VoucherRemoveIcon size={20} />
              </Box>
            )}
            {isMobileScreen && <ChevronRight width={16} height={16} fillColor={'#8E8E8E'} />}
          </Box>
        </VoucherWrapper>
      )}
      {hasProductInBasket && isLessThanMinSpend && <BasketMinimumOrderBanner />}
      <Box>
        {hasProductInBasket && !showBasketSummaryModal && (
          <Box
            padding={isMobileScreen ? theme.spacing(1.5, 2, 0, 2) : theme.spacing(2, 4, 0, 4)}
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            gap={theme.spacing(1)}
          >
            <Checkbox
              name="isDefault"
              onChange={onClickSelectAllItems}
              defaultChecked={isSelectedAllBasketItems}
              checked={isSelectedAllBasketItems}
              disabled={loading}
              data-test-id={'basket_select_all_checkbox'}
              sx={{
                '&.Mui-disabled': {color: theme.palette.primary.light},
                color: '#C7C7C7',
                '&.Mui-checked': {
                  color: 'primary'
                },
                height: 'fit-content',
                padding: 0
              }}
            />
            <Typography component="p" variant="body2">
              {isSelectedAllBasketItems ? t('flexiBasket.deselectAll') : t('flexiBasket.selectAll')}
            </Typography>
          </Box>
        )}

        <StyleFooterContentWrapper isReactNativeView={isMobileScreen}>
          {!!basketPrice && hasProductInBasket && (
            <Box display="flex" flexDirection="column">
              <Box>
                <Box display="flex" alignItems="center" gap="4px">
                  <BasketTotalPriceV2 data-test-total-price="" component="p" id="lbl_total_exculd_delivery_fee">
                    <b>
                      {formatPriceWithCurrency(selectedItems?.length ? basketPrice.amount : 0, basketPrice.currency)}
                    </b>
                  </BasketTotalPriceV2>
                  <Box
                    onClick={() => {
                      !showBasketSummaryModal && openBasketSummaryAnalytics(ANALYTICS.BASKET_SUMMARY_OPEN, {});
                      !loading && setShowBasketSummaryModal(!showBasketSummaryModal);
                    }}
                    sx={{cursor: 'pointer', display: 'flex'}}
                  >
                    <ChevronUpDownIcon />
                  </Box>
                </Box>
              </Box>
              {!!showDeliveryFee && hasProductInBasket && !!selectedItems?.length && (
                <>
                  {deliveryFees === '0.00 ฿' ? (
                    <DeliveryFeesLabel data-test-delivery-fee="" display="flex">
                      <DeliveryFeesLabel sx={{color: theme.palette.success.main}}>
                        {t('label.free')}&nbsp;
                      </DeliveryFeesLabel>
                      {t('label.delivery')}
                    </DeliveryFeesLabel>
                  ) : (
                    <DeliveryFeesLabel data-test-delivery-fee="">
                      {t('message.deliveryFeeIncludedNew', {fees: deliveryFees})}
                    </DeliveryFeesLabel>
                  )}
                </>
              )}
            </Box>
          )}

          <Box>
            <Button
              data-test-checkout-btn=""
              disabled={disableCheckoutBtn || loading || !selectedItems?.length}
              fullWidth
              onClick={onCheckoutClick}
              type="button"
              color="primary"
              loading={loading}
              variant="contained"
              shape="rounded"
              id="btn_go_to_checkout"
            >
              {actionButtonLabel}
              {hasProductInBasket ? ` (${selectedItems?.length || 0})` : ''}
            </Button>
          </Box>
        </StyleFooterContentWrapper>
        {shouldShowMinBasketWarning && isLessThanMinSpend && (
          <Box paddingTop={1} paddingX={2.5} display="flex" justifyContent="center" gap={1} flexWrap="wrap">
            <Typography fontWeight={400} fontSize={12}>
              {t('recommendations.minimumOrder', {amount: minimumBasket})}
            </Typography>
            <Box display="flex" alignItems="center" onClick={toggleMinimalBasketModal}>
              <Typography fontWeight={400} fontSize={12} color="common.mds_global_color_red_25">
                {t('recommendations.readMore')}
              </Typography>
              <ChevronRight
                width={12}
                height={12}
                fillColor={themeConfig.palette?.common?.mds_global_color_red_25}
              ></ChevronRight>
            </Box>
          </Box>
        )}
      </Box>
      <MinimumBasketAmountModal
        open={isMinimalBasketModalOpen}
        onClose={toggleMinimalBasketModal}
        isReactNativeWebView={!!isMobileScreen}
      />

      <RemoveVoucherModal
        open={showVoucherRemovalModal}
        setShowRemovalModal={setShowVoucherRemovalModal}
        onConfirm={handleConfirmRemoveVoucher}
        isReactNativeWebView={!!isMobileScreen}
        loading={removingVoucher}
      />
    </StyledBasketFooterWrapper>
  );
};

FooterView.displayName = 'FooterView';

export default FooterView;
